import { useState, useContext } from "react";
import { useLocation } from "react-router-dom";
import { type SxProps, type Theme } from "@mui/material/styles";

import { Box } from "ui/box";
import { Stack } from "ui/stack";
import { Divider } from "ui/divider";
import { Button } from "ui/button";
import { Collapse } from "ui/collapse";

import TruncatedText from "components/partials/truncated-text/truncated-text";
import { CommentIcons } from "containers/admin/clients/touchpoint/components/comments/comment-icons/comment-icons";
import { CommentInput } from "containers/admin/clients/touchpoint/components/comments/comment-input/comment-input";
import { CommentReplyDisplay } from "containers/admin/clients/touchpoint/components/comments/comment-reply-display/comment-reply-display";
import { CommentDisplayByline } from "containers/admin/clients/touchpoint/components/comments/comment-display-by-line/comment-display-by-line";
import { CommentPopoverButton } from "containers/admin/clients/touchpoint/components/comments/comment-popover-button/comment-popover-button";
import CommentActionsCard from "containers/admin/clients/touchpoint/components/comments/comment-actions-card/comment-actions-card";

import CommentContext from "contexts/comment-context";

import { useCurrentUser } from "hooks/api/use-current-user";

import { formatDateTime } from "utilities/date";
import { hasPermission } from "utilities/user";
import { Route } from "utilities/app-routes";

import { Permission } from "types/auth";
import { Comment, NewComment, CommentReply } from "types/comments";

import Icons from "assets";

interface CommentDisplayProps {
  comment: Comment;
  highlight: boolean;
  isClientUser: boolean;
}

const INLINE_TRIGGER_SX: SxProps<Theme> = { position: "relative", top: 0, left: 0, mr: 1 };

export const CommentDisplay = ({ comment, highlight, isClientUser }: CommentDisplayProps) => {
  const { submitNewCommentReply, loadCommentReplies, updateComment } = useContext(CommentContext);
  const [replyInputOpen, setReplyInputOpen] = useState<boolean>(false);
  const [showReplies, setShowReplies] = useState<boolean>(false);
  const [replies, setReplies] = useState<CommentReply[]>([]);
  const currentUser = useCurrentUser();
  const canComment = hasPermission(currentUser, Permission.PERM_COMMENT_WRITE);
  const currentLocation = useLocation().pathname.split("/");
  const canAnnotate = `/${currentLocation[currentLocation.length - 1]}` === Route.preview;

  const handleSubmitReply = (newComment: NewComment): Promise<CommentReply | void> => {
    return submitNewCommentReply(newComment).then((res) => {
      // if replies is not open, then load replies. otherwise, add to replies
      if (!showReplies) {
        setShowReplies(true);
        loadCommentReplies(comment).then((res) => {
          setReplies(res.items);
        });
      } else {
        if (res) {
          setReplies((prevReplies) => [...prevReplies, res]);
        }
      }
    });
  };

  const toggleOpenComment = () => {
    const isFlagged = comment.open ? !comment.actionItem : false;
    updateComment({ ...comment, open: !comment.open, actionItem: isFlagged });
  };
  const toggleFlagComment = () => {
    updateComment({ ...comment, actionItem: !comment.actionItem });
  };

  const toggleShowReplies = () => {
    if (!showReplies) {
      loadCommentReplies(comment).then((res) => setReplies(res.items));
    }
    setShowReplies((showReplies) => !showReplies);
  };

  const CommentDisplayHeader = () => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        borderBottom: "1px solid",
        borderBottomColor: "grey.200",
        fontSize: "0.75rem",
        letterSpacing: "0.025em",
        py: comment.annotationId ? "0.5rem" : "0.75rem",
      }}>
      {comment.annotationId && comment.badgeNumber && (
        <CommentPopoverButton
          sx={INLINE_TRIGGER_SX}
          comment={comment}
          isVisible={true}
          onClick={() => () => setShowReplies(false)}
          disabled={!canAnnotate}
        />
      )}

      <p className="flex gap-1 text-gray-600">
        {comment.version && (
          <>
            <span>v{comment.version}</span>
            <span>•</span>
          </>
        )}
        {formatDateTime(comment.createdDate)}
      </p>

      <CommentIcons
        canOpen={canComment && !isClientUser}
        canFlag={canComment && comment.open}
        isChecked={!comment.open}
        isFlagged={comment.actionItem}
        sx={{ ml: "auto" }}
        toggleFlag={toggleFlagComment}
        toggleCheck={toggleOpenComment}
      />
    </Box>
  );

  return (
    <Box
      data-testid="comment-display"
      sx={{
        pb: "1rem",
        borderBottom: "1px solid",
        borderBottomColor: "grey.500",
        backgroundColor: highlight ? "grey.100" : "white",
      }}>
      <Box sx={{ px: "1.5rem" }}>
        <CommentDisplayHeader />

        <Box sx={{ opacity: comment.open ? 1 : 0.5 }}>
          <CommentDisplayByline user={comment.user} />
          <Stack
            spacing={1}
            divider={<Divider sx={{ borderColor: "grey.200" }} />}
            className="content"
            sx={{ pl: "1.5rem" }}>
            <TruncatedText text={comment.text} noOfLines={4} initialTruncate={true} />

            <CommentActionsCard
              isClientUser={isClientUser}
              comment={comment}
              setReplyInputOpen={setReplyInputOpen}
            />

            {/* Add a Comment Input */}
            {replyInputOpen && (
              <CommentInput
                parentId={comment.id}
                submitNewComment={handleSubmitReply}
                commentType="text-only"
                isClientUser={isClientUser}
                isDisabled={!canComment || !comment.open}
              />
            )}

            {/* Comment Replies */}
            {comment.replyCount > 0 && (
              <Box>
                <Collapse in={showReplies}>
                  <Stack spacing={1} sx={{ mb: "0.5rem" }}>
                    {replies.map((reply: CommentReply) => (
                      // Per reply, so one posted while the list is already open still grows in.
                      <Collapse key={reply.id} in appear>
                        <CommentReplyDisplay reply={reply} />
                      </Collapse>
                    ))}
                  </Stack>
                </Collapse>
                {/* Show / Hide Comment Replies */}
                {showReplies ? (
                  <Button
                    variant="text"
                    endIcon={<Icons.ChevronUp />}
                    onClick={toggleShowReplies}>
                    Hide {comment.replyCount} replies
                  </Button>
                ) : (
                  <Button
                    variant="text"
                    endIcon={<Icons.ChevronDown />}
                    onClick={toggleShowReplies}>
                    Show {comment.replyCount} replies
                  </Button>
                )}
              </Box>
            )}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};
